// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { Box, Button, Text } from 'theme-ui'
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from 'react-oidc-context'
import PropTypes from 'prop-types'
import Url from '../../../util/url'

// Options mirror "Крок 0" of the narrative-agent system prompt.
const PURPOSE_OPTIONS = [
  'Рекомендації для міської ради / виконкому',
  'Звіт для мешканців громади',
  'Підготовка публічного обговорення / слухань',
  'Медіа-публікація / прес-реліз',
  'Внутрішній аналіз команди / звіт для партнерів',
  'Академічна стаття або дослідження'
]

const STYLE_OPTIONS = [
  ['analytical', 'Аналітичний (факти + висновки, для фахівців)'],
  ['executive', 'Executive summary (стисло, для керівників)'],
  ['public', 'Публічний звіт (без жаргону, для мешканців)'],
  ['academic', 'Науковий (статистика, методологія)']
]

const LANGUAGE_OPTIONS = [
  ['uk', 'Українська'],
  ['en', 'Англійська'],
  ['bilingual', 'Двомовний (укр + eng)']
]

const MODEL_OPTIONS = [
  ['claude', 'Claude']
]

const labelSx = { fontSize: [1], fontWeight: 'bold', color: 'text', mb: [1], mt: [2] }
const fieldStyle = {
  display: 'block',
  width: '100%',
  maxWidth: 520,
  padding: '6px 8px',
  fontSize: 14,
  border: '1px solid #cbd5e0',
  borderRadius: 4,
  marginBottom: 4
}

const NarrativeAgentForm = ({ reportId, defaultTopic }) => {
  const { user: authUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    topic: defaultTopic || '',
    audience: '',
    purpose: PURPOSE_OPTIONS[0],
    style: 'public',
    format: 'docx',
    language: 'uk',
    model: 'claude',
    vote_threshold: 50
  })
  const [status, setStatus] = useState('idle') // idle | generating | done | error
  const [errorMsg, setErrorMsg] = useState(null)
  const [prevReports, setPrevReports] = useState([])

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const authHeaders = useCallback(() => {
    const token = authUser ? authUser.access_token : null
    return {
      Accept: 'application/json, application/octet-stream, */*',
      ...(token && { Authorization: `Bearer ${token}` })
    }
  }, [authUser])

  const fetchReportsList = useCallback(async () => {
    const resp = await fetch(
      `${Url.urlPrefix}api/v3/reportNarrativeAgentList?report_id=${encodeURIComponent(reportId)}`,
      { credentials: 'include', headers: authHeaders() }
    )
    if (!resp.ok) return []
    const j = await resp.json()
    return (j && j.reports) || []
  }, [reportId, authHeaders])

  const loadPrevReports = useCallback(async () => {
    try {
      setPrevReports(await fetchReportsList())
    } catch {
      /* ignore */
    }
  }, [fetchReportsList])

  useEffect(() => {
    if (open) loadPrevReports()
  }, [open, loadPrevReports])

  const downloadPrev = async (r) => {
    try {
      const resp = await fetch(
        `${Url.urlPrefix}api/v3/reportNarrativeAgentFile?report_id=${encodeURIComponent(
          reportId
        )}&ts=${encodeURIComponent(r.ts)}&name=${encodeURIComponent(r.filename)}`,
        { credentials: 'include', headers: authHeaders() }
      )
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = await resp.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = r.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      setErrorMsg(String((e && e.message) || e))
    }
  }

  const fmtTs = (ts) => {
    // ts is "YYYYMMDD-HHMMSS"
    const m = /^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/.exec(ts || '')
    return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : ts
  }

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

  const pollForReport = async (ts) => {
    const deadline = Date.now() + 15 * 60 * 1000 // 15 min ceiling
    while (Date.now() < deadline) {
      await sleep(8000)
      let list = []
      try {
        list = await fetchReportsList()
      } catch {
        continue
      }
      setPrevReports(list)
      const entry = list.find((r) => r.ts === ts)
      if (entry && entry.filename) {
        await downloadPrev(entry)
        setStatus('done')
        return
      }
      if (entry && entry.error) {
        throw new Error('Генерація не вдалася: ' + entry.error)
      }
    }
    throw new Error(
      'Перевищено час очікування. Звіт може зʼявитися у «Попередні звіти» трохи згодом — оновіть перелік.'
    )
  }

  const generate = async () => {
    setStatus('generating')
    setErrorMsg(null)
    try {
      const token = authUser ? authUser.access_token : null
      const resp = await fetch(`${Url.urlPrefix}api/v3/reportNarrativeAgent`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, */*',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ report_id: reportId, ...form })
      })
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`
        try {
          const j = await resp.json()
          if (j && (j.error || j.message)) msg = j.error || j.message
        } catch {
          /* non-json error body */
        }
        throw new Error(msg)
      }
      // Async: the server returns 202 + { ts, filename } immediately and
      // generates in the background. Poll the list until the file is ready —
      // this avoids the Cloudflare 100s proxy timeout (HTTP 524).
      const j = await resp.json().catch(() => null)
      if (j && j.ts) {
        await pollForReport(j.ts)
        return
      }
      throw new Error('Неочікувана відповідь сервера (немає ідентифікатора задачі).')
    } catch (e) {
      setErrorMsg(String((e && e.message) || e))
      setStatus('error')
    }
  }

  const generating = status === 'generating'

  return (
    <Box sx={{ mt: [3], pt: [3], borderTop: '1px solid #e2e8f0' }}>
      <Button
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        sx={{ bg: '#1B3A6B' }}>
        {open ? '▾ ' : '▸ '}Згенерувати наративний звіт
      </Button>

      {open && (
        <Box sx={{ mt: [3], maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
          <Text sx={labelSx}>Тема дискусії</Text>
          <input style={fieldStyle} value={form.topic} onChange={set('topic')} placeholder="Напр.: Розвиток громадського транспорту" />

          <Text sx={labelSx}>Аудиторія та охоплення</Text>
          <input style={fieldStyle} value={form.audience} onChange={set('audience')} placeholder="Напр.: мешканці Житомира, 796 учасників, жовтень 2025" />

          <Text sx={labelSx}>Для кого / навіщо аналіз</Text>
          <select style={fieldStyle} value={form.purpose} onChange={set('purpose')}>
            {PURPOSE_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          <Text sx={labelSx}>Стиль подачі</Text>
          <select style={fieldStyle} value={form.style} onChange={set('style')}>
            {STYLE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>

          <Text sx={labelSx}>Мова звіту</Text>
          <select style={fieldStyle} value={form.language} onChange={set('language')}>
            {LANGUAGE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>

          <Text sx={labelSx}>Модель</Text>
          <select style={fieldStyle} value={form.model} onChange={set('model')}>
            {MODEL_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>

          <Text sx={labelSx}>Поріг голосів (мінімум для аналізу)</Text>
          <input
            style={fieldStyle}
            type="number"
            min={1}
            value={form.vote_threshold}
            onChange={set('vote_threshold')}
          />

          <Box sx={{ mt: [3] }}>
            <Button onClick={generate} disabled={generating} sx={{ bg: generating ? '#8aa' : '#1A7A4A' }}>
              {generating ? 'Генерується…' : 'Згенерувати'}
            </Button>
          </Box>

          {status === 'generating' && (
            <Text sx={{ fontSize: [1], color: '#2E5FA3', mt: [2] }}>
              Аналіз даних і генерація звіту — це може зайняти кілька хвилин. Не
              закривайте вкладку: завантаження почнеться автоматично, щойно звіт
              буде готовий.
            </Text>
          )}
          {status === 'done' && (
            <Text sx={{ fontSize: [1], color: '#1A7A4A', fontWeight: 'bold', mt: [2] }}>
              Готово ✓ — звіт згенеровано
            </Text>
          )}
          {status === 'error' && (
            <Text sx={{ fontSize: [1], color: '#C0392B', fontWeight: 'bold', mt: [2] }}>
              Помилка: {errorMsg}
            </Text>
          )}

          <Box sx={{ mt: [4], pt: [3], borderTop: '1px solid #e2e8f0' }}>
            <Text sx={{ fontSize: [1], fontWeight: 'bold', color: 'text', mb: [2] }}>
              Попередні звіти ({prevReports.length})
            </Text>
            {prevReports.length === 0 ? (
              <Text sx={{ fontSize: [0], color: 'textSecondary' }}>
                Ще немає збережених звітів для цього report id.
              </Text>
            ) : (
              prevReports.map((r) => (
                <Box
                  key={r.ts + '/' + r.filename}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: [1] }}>
                  <Text sx={{ fontSize: [0], color: 'textSecondary', minWidth: 130 }}>
                    {fmtTs(r.ts)}
                  </Text>
                  <Button
                    onClick={() => downloadPrev(r)}
                    sx={{
                      bg: 'transparent',
                      color: '#2E5FA3',
                      border: '1px solid #2E5FA3',
                      fontSize: 0,
                      padding: '3px 10px'
                    }}>
                    ⭳ {r.filename} ({Math.round((r.size || 0) / 1024)} КБ)
                  </Button>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

NarrativeAgentForm.propTypes = {
  reportId: PropTypes.string.isRequired,
  defaultTopic: PropTypes.string
}

export default NarrativeAgentForm
